"use client"

import Link from "next/link"
import { useTranslations } from "next-intl"

import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { PageChrome } from "@/components/layout/PageChrome"
import { CitationNetwork } from "@/components/marketing/CitationNetwork"
import { SUBSCRIPTIONS_OPEN } from "@/config/launch"
import { cn } from "@/lib/utils"

/**
 * The landing page.
 *
 * Replaces the long-form pitch, which is parked at /features — see DeprecatedLandingPage. That
 * page argued; this one shows. The two things a visitor takes away are the size of the corpus
 * and the shape of it, and both are drawn from the corpus itself rather than asserted.
 *
 * ─── No screenshots ────────────────────────────────────────────────────────────────────────
 *
 * Deliberate, and the main constraint on the design. The product UI is not public yet, so
 * there is no picture of the app anywhere here — which is also why the old page's three
 * `AppScreenshot` figures did not come across: they were abstract wireframes standing in for
 * screenshots nobody had taken, and an invented picture of an interface is worse than none.
 *
 * What carries the visual weight instead is the citation network, which is real data and gives
 * away nothing about the interface.
 *
 * ─── Matching the app ──────────────────────────────────────────────────────────────────────
 *
 * The type here is the research canvas's, not a marketing scale invented for this page:
 * `font-light` with tight tracking, which is what components/research/ResearchCanvas.tsx uses
 * for its own headings. The old page was `font-serif` at heavy weights and read as a different
 * product. Buttons are the app's Button, so they are the app's pill; cards are the app's Card.
 *
 * Colour follows the same rule as the rest of the app: greyscale, with the blue spent sparingly
 * — see the note on `--primary` in globals.css. On this page the blue is spent in exactly two
 * places, the case-law points in the network and the stat figures.
 */

/*
  Counted from the live corpus on 2026-09-24. See the note on CORPUS_SIZE in
  research/FeatureStrip.tsx, which carries the same figure and the full breakdown.

  The documents figure is 246,434 — the substantive documents in `document_lookup`, being
  57,547 case-law documents and 188,887 legislative acts. It is NOT the table's 333,295 rows.
  Of those, 58,950 are Official Journal notices, which announce that a case exists rather than
  being anything to read, and 27,884 are consolidated versions of acts already counted. A
  headline that counted both would be a third larger and would not survive being checked.

  Rounded down hard, and for two reasons. The loader runs continuously, so an exact figure is
  wrong the next morning. And a round number that is comfortably true survives someone
  checking it, which a precise one does not.
*/
const STATS = [
  { value: "245,000+", labelKey: "statDocuments" },
  { value: "26,000+", labelKey: "statJudgments" },
  { value: "900,000+", labelKey: "statCitations" },
  { value: "70+", labelKey: "statYears" },
] as const

/** The eight feature cards. Keys under `pages.landing`; see the note on the grid below. */
const FEATURES = ["f1", "f2", "f3", "f4", "f5", "f6", "f7", "f8"] as const

// ─── Shared section primitives ───────────────────────────────────────────────────────────────

/** The small letterspaced label above a section heading. */
function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
      {children}
    </p>
  )
}

/**
 * A section's own band.
 *
 * `full` on PageChrome means the shell draws no column, so each section draws its own
 * background edge to edge and centres its own 6xl measure inside it. `tinted` alternates onto
 * --secondary, which is the app's one step off the background.
 */
function Section({
  children,
  tinted = false,
  className,
}: {
  children: React.ReactNode
  tinted?: boolean
  className?: string
}) {
  return (
    <section className={cn("w-full px-6", tinted && "bg-secondary", className)}>
      <div className="mx-auto w-full max-w-6xl py-20 md:py-28">{children}</div>
    </section>
  )
}

// ─── Hero ────────────────────────────────────────────────────────────────────────────────────

/**
 * The hero.
 *
 * Copy comes from `pages.features`, not `pages.landing`: it is the wording the previous
 * landing page used, kept deliberately. The `heroHeadline` / `heroBody` keys written for this
 * page are still in the message files, unused, if it is ever wanted back.
 *
 * Only the words are the old page's. The type is still the app's — `font-light` with tight
 * tracking, as on the research canvas — rather than the serif the old page set this in.
 */
function Hero() {
  const t = useTranslations("pages.features")
  const tl = useTranslations("pages.landing")
  return (
    <section className="w-full px-6">
      {/* Taller than the sections below it and top-weighted, so the headline sits where the eye
          lands rather than centred in a band of its own. The top padding also clears the fixed
          header, which the scrollport runs underneath. */}
      <div className="mx-auto w-full max-w-6xl pb-20 pt-28 md:pb-28 md:pt-40">
        <h1 className="max-w-4xl text-4xl font-light leading-[1.08] tracking-tight text-foreground md:text-6xl">
          {t("powerfulIntuitiveLegalResearchTools")}
        </h1>
        {/* Wider measure than the headline's: this paragraph is four sentences where the new
            copy was two, and at max-w-2xl it ran to nine lines. */}
        <p className="mt-10 max-w-3xl text-base font-normal leading-relaxed text-muted-foreground md:text-lg">
          {t("cuttingEdgePlatform")}
        </p>
        {/* Hidden while sales are closed — /pricing is behind the access gate. See
            config/launch.ts, and the same guard on the header's Subscribe button. */}
        {SUBSCRIPTIONS_OPEN && (
          <div className="mt-10">
            <Button size="lg" variant="outline" asChild>
              <Link href="/pricing">{tl("heroCta")}</Link>
            </Button>
          </div>
        )}
      </div>
    </section>
  )
}

// ─── Stat bar ────────────────────────────────────────────────────────────────────────────────

function StatBar() {
  const t = useTranslations("pages.landing")
  return (
    <Section tinted>
      <div className="flex flex-col gap-10">
        <Eyebrow>{t("statsEyebrow")}</Eyebrow>
        {/*
          Two columns on a phone rather than four: four figures of this size on a 375px screen
          wrap their labels onto three lines each and stop reading as a row of numbers.
        */}
        <dl className="grid grid-cols-2 gap-x-8 gap-y-10 md:grid-cols-4">
          {STATS.map((stat) => (
            <div key={stat.labelKey} className="flex flex-col gap-2">
              <dt className="sr-only">{t(stat.labelKey)}</dt>
              <dd className="text-3xl font-light leading-none tracking-tight text-[var(--accent-blue)] md:text-5xl">
                {stat.value}
              </dd>
              <p className="text-[13px] font-[350] text-muted-foreground">{t(stat.labelKey)}</p>
            </div>
          ))}
        </dl>
        <p className="max-w-2xl text-[13px] font-[350] leading-relaxed text-muted-foreground">
          {t("statsNote")}
        </p>
      </div>
    </Section>
  )
}

// ─── The citation network ────────────────────────────────────────────────────────────────────

function NetworkSection() {
  const t = useTranslations("pages.landing")
  return (
    <Section>
      <div className="flex flex-col gap-10">
        <div className="flex max-w-3xl flex-col gap-6">
          <Eyebrow>{t("networkEyebrow")}</Eyebrow>
          <h2 className="text-3xl font-light leading-tight tracking-tight text-foreground md:text-4xl">
            {t("networkHeading")}
          </h2>
          <p className="text-base font-normal leading-relaxed text-muted-foreground">
            {t("networkBody")}
          </p>
        </div>
        <CitationNetwork
          caption={t("networkCaption")}
          unavailableLabel={t("networkUnavailable")}
          legend={{
            hubs: t("legendHubs"),
            caseLaw: t("legendCaseLaw"),
            legislation: t("legendLegislation"),
          }}
        />
      </div>
    </Section>
  )
}

// ─── Feature grid ────────────────────────────────────────────────────────────────────────────

function FeatureGrid() {
  const t = useTranslations("pages.landing")
  return (
    <Section tinted>
      <div className="flex flex-col gap-14">
        <div className="flex max-w-3xl flex-col gap-6">
          <Eyebrow>{t("featuresEyebrow")}</Eyebrow>
          <h2 className="text-3xl font-light leading-tight tracking-tight text-foreground md:text-4xl">
            {t("featuresHeading")}
          </h2>
          <p className="text-base font-normal leading-relaxed text-muted-foreground">
            {t("featuresBody")}
          </p>
        </div>
        {/*
          The app's Card, so the corners, the border and the surface are the ones used on
          /preferences and /projects rather than a second card style invented here. `bg-card`
          reads as a raised tile against this section's --secondary band and would be invisible
          against the plain background, which is why the grid sits in a tinted section.
        */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((id) => (
            <Card key={id} className="gap-3 px-6">
              <h3 className="text-[15px] font-medium leading-snug tracking-[-0.01em] text-foreground">
                {t(`${id}Title`)}
              </h3>
              <p className="text-[13px] font-[350] leading-relaxed text-muted-foreground">
                {t(`${id}Body`)}
              </p>
            </Card>
          ))}
        </div>
      </div>
    </Section>
  )
}

// ─── Purpose ─────────────────────────────────────────────────────────────────────────────────

/** Copy reused from the old page, which is the only place this argument is written down. */
function Purpose() {
  const t = useTranslations("pages.features")
  const tl = useTranslations("pages.landing")
  return (
    <Section>
      <div className="flex max-w-3xl flex-col gap-6">
        <Eyebrow>{tl("purposeEyebrow")}</Eyebrow>
        <h2 className="text-3xl font-light leading-tight tracking-tight text-foreground md:text-4xl">
          {t("ourPurpose")}
        </h2>
        <p className="text-base font-normal leading-relaxed text-muted-foreground">
          {t("lawlibraryAiWasFoundedBy")}
        </p>
        <p className="text-base font-normal leading-relaxed text-muted-foreground">
          {t("accessToJustice")}
        </p>
        <p className="text-base font-normal leading-relaxed text-muted-foreground">
          {t("ourApproachToTheUse")}
        </p>
      </div>
    </Section>
  )
}

// ─── Team ────────────────────────────────────────────────────────────────────────────────────

const TEAM = [
  {
    name: "Josh Fjelstul, CEO",
    photo: "/bio-photos/fjelstul.jpg",
    bio: "Josh is a political economist, data scientist, and machine learning engineer who specializes in EU law and the politics of the CJEU. He has a BBA from the Canfield Business Honors Program at the McCombs School of Business at the University of Texas at Austin and a Ph.D. in Political Science from Emory University.",
  },
  {
    name: "Daniel Naurin, COO",
    photo: "/bio-photos/naurin.jpg",
    bio: "Daniel is a political scientist who specializes in EU law and the politics of the CJEU. He's a Professor of Political Science and the Director of the ARENA Centre for European Studies at the University of Oslo. He's affiliated with the University of Gothenburg where he leads work on the IUROPA CJEU Database.",
  },
  {
    name: "Johan Lindholm, CPO",
    photo: "/bio-photos/lindholm.jpg",
    bio: "Johan is a legal scholar who specializes in EU law, constitutional law, comparative law, sports law, and judicial politics. He's a Professor of Law at Umeå University and the Editor-in-Chief of the European Journal of Empirical Legal Studies.",
  },
] as const

function Team() {
  const t = useTranslations("pages.features")
  return (
    <Section tinted>
      <div className="flex flex-col gap-14">
        <div className="flex max-w-3xl flex-col gap-6">
          <h2 className="text-3xl font-light leading-tight tracking-tight text-foreground md:text-4xl">
            {t("ourTeam")}
          </h2>
          <p className="text-base font-normal leading-relaxed text-muted-foreground">
            {t("teamIntro")}
          </p>
        </div>
        <div className="grid grid-cols-1 gap-12 md:grid-cols-3">
          {TEAM.map((member) => (
            <div key={member.name} className="flex flex-col gap-5">
              {/* Greyscale like the old page: three photographs at full saturation would be the
                  only colour on the page apart from the network, and would out-shout it. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={member.photo}
                alt={member.name}
                className="size-24 rounded-full object-cover grayscale"
              />
              <p className="text-[15px] font-medium tracking-[-0.01em] text-foreground">
                {member.name}
              </p>
              <p className="text-[13px] font-[350] leading-relaxed text-muted-foreground">
                {member.bio}
              </p>
            </div>
          ))}
        </div>
      </div>
    </Section>
  )
}

// ─── Collaborate ─────────────────────────────────────────────────────────────────────────────

function Collaborate() {
  const t = useTranslations("pages.features")
  return (
    <Section>
      <div className="flex max-w-3xl flex-col items-start gap-6">
        <h2 className="text-3xl font-light leading-tight tracking-tight text-foreground md:text-4xl">
          {t("collaborateWithUs")}
        </h2>
        <p className="text-base font-normal leading-relaxed text-muted-foreground">
          {t("apiOffer")}
        </p>
        {/* The one live call to action on the page while subscriptions are closed, and it
            leaves the site, so the access gate cannot swallow it. */}
        <Button size="lg" variant="outline" asChild className="mt-4">
          <Link href="mailto:joshua.fjelstul@lawlibrary.ai">{t("contactUs")}</Link>
        </Button>
      </div>
    </Section>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────────────────────

export function LandingPage() {
  return (
    <div className="w-full">
      <PageChrome width="full" />
      <Hero />
      <StatBar />
      <NetworkSection />
      <FeatureGrid />
      <Purpose />
      <Team />
      <Collaborate />
    </div>
  )
}
